import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserUtil } from '../../../../data/utils/user.utils';
import { Show } from '../../models/show.interface';
import { CreateShowDto, UpdateShowDto } from '../../dtos';
import { FileService } from 'src/app/data/services/file.service';
import { FileDto } from 'src/app/data/models/dto/file.dto';
import { BookingParamsDto } from '../../dtos/booking-params.dto';
import { BookingsService } from '../../services/bookings.service';
import { SimpleUserDto } from 'src/app/data/models/dto/simple-user.dto';
import { BookingStatus } from '../../enums/booking-status.enum';

@Component({
  selector: 'app-show-form',
  templateUrl: './show-form.component.html',
  styleUrls: ['./show-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ShowFormComponent implements OnInit {
  @Output() onUpdateShow = new EventEmitter<UpdateShowDto>();
  @Output() onCreateShow = new EventEmitter<CreateShowDto>();

  bgFilesToDelete: FileDto[] = [];

  get isUpdate() {
    return this.show?.id ? true : false;
  }

  show?: Show = this.data.show;

  today: Date = new Date();

  disabled = true;

  user: SimpleUserDto | undefined;

  firstFormGroup: FormGroup = this.fb.group({
    title: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    description: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    place: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    startDate: new FormControl({ value: '', disabled: false }, [
      Validators.required,
      Validators.minLength(1),
    ]),
    endDate: new FormControl({ value: '', disabled: false }, [
      Validators.required,
      Validators.minLength(1),
    ]),
    deadlineDate: new FormControl({ value: '', disabled: false }, [
      Validators.required,
      Validators.minLength(1),
    ]),
    urlImage: new FormControl({ value: '', disabled: false }),
  });

  secondFormGroup: FormGroup = this.fb.group({
    idsProfiles: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    idsClassrooms: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    idsProfilesText: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    idsClassroomsText: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    year: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
  });

  thirdFormGroup: FormGroup = this.fb.group({
    maxSeatBookings: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    row: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    column: new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]),
    seats: new FormControl({ value: '', disabled: false }),
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly userUtil: UserUtil,
    private readonly fileService: FileService,
    private readonly bookingService: BookingsService,
    @Inject(MAT_DIALOG_DATA)
    private readonly data: {
      show?: Show;
    },
  ) { }

  async ngOnInit() {
    this.disabled = false
    this.user = this.userUtil.getUser();
    if (this.isUpdate) {
      this.firstFormGroup.setValue({
        title: this.show?.title,
        description: this.show?.description,
        place: this.show?.place,
        startDate: this.show?.startDate,
        endDate: this.show?.endDate,
        deadlineDate: this.show?.deadlineDate,
        urlImage: this.show?.urlImage
      });
      this.secondFormGroup.setValue({
        idsProfiles: this.show?.idsProfiles,
        idsProfilesText: '',
        idsClassrooms: this.show?.idsClassrooms,
        idsClassroomsText: '',
        year: this.show?.year

      })
      this.thirdFormGroup.setValue({
        maxSeatBookings: this.show?.maxSeatBookings,
        column: this.show?.seats[0].length,
        row: this.show!.seats!.flat()[this.show!.seats!.flat().length - 1].row,
        seats: this.show?.seats,
      });

      this.thirdFormGroup.controls['row'].disable();
      this.thirdFormGroup.controls['column'].disable();

      let booked = await this.getOneBooking(this.show!.id)

      if (new Date(this.show!.startDate) <= this.today || booked) {
        if (!booked){
          this.firstFormGroup.disable()
          this.disabled = true;
        }
        this.secondFormGroup.disable()
        this.thirdFormGroup.disable()
      }
    }
  }

  private get schools() {
    return this.userUtil.getSchools();
  }

  private buildParams(
    idShow?: string,
    status?: BookingStatus
  ): BookingParamsDto {
    return {
      idShow: idShow,
      status: status
    }
  }

  async getOneBooking(idShow: string) {
    const params = this.buildParams(idShow, BookingStatus.BOOKED_SUCCESSFULLY);
    return (await this.bookingService
      .findOne(params)
      .toPromise()
      .then((response) => {
        if (response.id !== undefined) return true
        return false
      })
      .catch((ex) => {
        return false
      }))
  }

  formatForm() {
    let background: string | FileDto | null = null;
    if (this.firstFormGroup.controls['urlImage'].value)
      background = this.firstFormGroup.controls['urlImage'].value;
    return {
      title: this.firstFormGroup.controls['title'].value,
      description: this.firstFormGroup.controls['description'].value,
      urlImage: background,
      startDate: this.firstFormGroup.controls['startDate'].value,
      endDate: this.firstFormGroup.controls['endDate'].value,
      deadlineDate: this.firstFormGroup.controls['deadlineDate'].value,
      place: this.firstFormGroup.controls['place'].value,
      year: this.secondFormGroup.controls['year'].value,
      idsProfiles: this.secondFormGroup.controls['idsProfiles'].value,
      idsClassrooms: this.secondFormGroup.controls['idsClassrooms'].value,
      maxSeatBookings: this.thirdFormGroup.controls['maxSeatBookings'].value,
      totalSeats: (this.thirdFormGroup.controls['seats'].value).flat().length,
      seats: this.thirdFormGroup.controls['seats'].value,
      idSchool: this.schools[0].id
    }
  }

  deleteFiles(event: any) {
    this.bgFilesToDelete = event;
  }

  saveShow() {
    if (this.isUpdate) {
      let show: UpdateShowDto = this.formatForm();
      this.onUpdateShow.emit(show)
    }
    else {
      let show: CreateShowDto = this.formatForm();
      this.onCreateShow.emit(show)
    }

    this.bgFilesToDelete.forEach((f) => {
      this.fileService.delete(f.id!).toPromise();
    });
  }

  //#endregion
}
